import { useEffect, type RefObject } from "react";
import { Platform } from "react-native";
import type { View } from "react-native";

export interface UseClickOutsideOptions {
  /**
   * Which DOM event to listen for. `"mousedown"` fires before a subsequent
   * `click` can re-open the same popover (e.g. clicking the toggle button
   * again), so it is the right default for most popovers/dropdowns. Some
   * call sites intentionally use `"click"` instead — pass it explicitly to
   * preserve that behaviour.
   *
   * @default "mousedown"
   */
  eventType?: "mousedown" | "click";
}

/**
 * Calls `onClickOutside` when a press/click occurs outside the given
 * boundary, active only while `enabled` is true.
 *
 * - Web: attaches a listener to `document`.
 * - Native: no-op — use a transparent backdrop `Pressable` in the component
 *   tree instead (native has no global hit-test API).
 *
 * `boundary` is either:
 * - a ref to the container element (checked via `Node.contains`), or
 * - a CSS selector string, e.g. `"[data-sort-dropdown]"` (checked via
 *   `Element.closest`) — for call sites that mark their container with a
 *   data attribute instead of holding a ref.
 */
export function useClickOutside(
  boundary: RefObject<View | null> | string,
  onClickOutside: () => void,
  enabled: boolean,
  options: UseClickOutsideOptions = {},
): void {
  const { eventType = "mousedown" } = options;

  useEffect(() => {
    if (Platform.OS !== "web" || !enabled) return;

    function isOutside(target: Node | null): boolean {
      if (typeof boundary === "string") {
        return !(target instanceof Element) || !target.closest(boundary);
      }
      const node = boundary.current as unknown as HTMLElement | null;
      return node != null && !node.contains(target);
    }

    function handleEvent(event: MouseEvent) {
      if (isOutside(event.target as Node | null)) {
        onClickOutside();
      }
    }

    document.addEventListener(eventType, handleEvent);
    return () => {
      document.removeEventListener(eventType, handleEvent);
    };
  }, [boundary, onClickOutside, enabled, eventType]);
}
